import * as React from "react";
import {useState} from "react";
import {Button, Divider, Header, Icon, Label, Modal, Segment} from "semantic-ui-react";
import {DeferredNotification} from "../../../../../generated";
import {formatDate} from "../../../../../format";
import "./DeferredNotificationModal.css";

export interface DeferredNotificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    notification: DeferredNotification | null;
}

export function DeferredNotificationModal(props: DeferredNotificationModalProps) {

    const [showRawEmail, setShowRawEmail] = useState(false);
    const [overlayEnabled, setOverlayEnabled] = useState(true);

    function handleClose() {
        props.onClose();
        setShowRawEmail(false);
        setOverlayEnabled(true);
    }


    if (!props.notification) {
        return null;
    }

    const notification = props.notification;
    const isEmail = !!notification.emailNotification;
    const isSms = !!notification.smsNotification;

    return (
        <>
            <Modal
                open={props.isOpen}
                onClose={handleClose}
                dimmer="blurring"
                size='fullscreen'
                className="deferred-notification-modal">
                <Header icon={isEmail ? 'mail' : 'mobile'}
                        content={`Deferred ${isEmail ? 'Email' : 'SMS'} Notification`}/>
                <Modal.Content>
                    <Segment>
                        <Label ribbon>General Information</Label>
                        <div style={{marginTop: '10px'}}>
                            <p><strong>ID:</strong> {notification._id}</p>
                            <p><strong>Order ID:</strong> {notification.orderId}</p>
                            <p><strong>Company ID:</strong> {notification.companyId}</p>
                            <p><strong>Observer ID:</strong> {notification.observerId}</p>
                            <p><strong>Created:</strong> {formatDate(notification.created)}</p>
                            <p><strong>Deferred Until:</strong> {formatDate(notification.deferredUntil)}</p>
                            {notification.sent && <p><strong>Sent:</strong> {formatDate(notification.sent)}</p>}
                            <p><strong>Processed:</strong> {notification.processed ? 'Yes' : 'No'}</p>
                            <p><strong>Error Count:</strong> <span style={{
                                color: notification.errorCount > 0 ? 'red' : 'inherit',
                                fontWeight: notification.errorCount > 0 ? 'bold' : 'normal'
                            }}>{notification.errorCount || 0}</span></p>
                            {notification.collapseKey &&
                                <p><strong>Collapse Key:</strong> {notification.collapseKey}</p>}
                        </div>
                    </Segment>

                    <Divider/>

                    {isEmail && notification.emailNotification && (
                        <Segment>
                            <Label ribbon color="blue">Email Details</Label>
                            <div style={{marginTop: '10px'}}>
                                <p><strong>Title:</strong> {notification.emailNotification.title}</p>
                                <p><strong>Receivers:</strong> {notification.emailNotification.receivers?.join(', ')}
                                </p>
                                <Divider/>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '10px'
                                }}>
                                    <Label>Message:</Label>
                                    <div style={{display: 'flex', gap: '10px'}}>
                                        {!showRawEmail && (
                                            <Button
                                                size="small"
                                                icon
                                                labelPosition="left"
                                                color={overlayEnabled ? 'red' : 'green'}
                                                onClick={() => setOverlayEnabled(!overlayEnabled)}
                                            >
                                                <Icon name={overlayEnabled ? 'lock' : 'unlock'}/>
                                                {overlayEnabled ? 'Content Locked' : 'Content Unlocked'}
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            icon
                                            labelPosition="left"
                                            onClick={() => setShowRawEmail(!showRawEmail)}
                                        >
                                            <Icon name={showRawEmail ? 'eye' : 'code'}/>
                                            {showRawEmail ? 'Show Rendered' : 'Show Raw HTML'}
                                        </Button>
                                    </div>
                                </div>
                                {overlayEnabled && !showRawEmail && (
                                    <Segment
                                        style={{
                                            marginBottom: '10px',
                                            backgroundColor: '#fff3cd',
                                            borderLeft: '4px solid #ffc107',
                                            padding: '10px',
                                            marginTop: '10px'
                                        }}>
                                        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                            <Icon name="lock" style={{color: '#ff6b6b'}} />
                                            <div>
                                                <strong>This content is for viewing only</strong>
                                                <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#666'}}>
                                                    Click "Content Unlocked" to enable interaction
                                                </p>
                                            </div>
                                        </div>
                                    </Segment>
                                )}
                                <Segment
                                    className="email-content-segment"
                                    style={{
                                        marginTop: '10px',
                                        backgroundColor: '#f4f4f4',
                                        maxHeight: '600px',
                                        overflow: 'auto',
                                        width: '100%',
                                        position: 'relative'
                                    }}>
                                    {showRawEmail ? (
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            margin: 0,
                                            fontFamily: 'monospace',
                                            fontSize: '12px'
                                        }}>
                                            {notification.emailNotification.text}
                                        </pre>
                                    ) : (
                                        <>
                                            {overlayEnabled && (
                                                <div className="email-overlay-container">
                                                    <div className="email-overlay" />
                                                </div>
                                            )}
                                            <iframe
                                                srcDoc={notification.emailNotification.text || ''}
                                                style={{
                                                    width: '100%',
                                                    height: '600px',
                                                    border: 'none',
                                                    backgroundColor: '#fff'
                                                }}
                                                title="email-content"
                                                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                                            />
                                        </>
                                    )}
                                </Segment>
                            </div>
                        </Segment>
                    )}

                    {isSms && notification.smsNotification && (
                        <Segment>
                            <Label ribbon color="green">SMS Details</Label>
                            <div style={{marginTop: '10px'}}>
                                <p><strong>Receiver:</strong> {notification.smsNotification.receiver}</p>
                                <Divider/>
                                <Label>Message:</Label>
                                <Segment
                                    style={{marginTop: '10px', whiteSpace: 'pre-wrap', backgroundColor: '#f4f4f4'}}>
                                    {notification.smsNotification.text}
                                </Segment>
                            </div>
                        </Segment>
                    )}
                </Modal.Content>
            </Modal>
        </>
    );
}
